import React from "react";
import PropTypes from "prop-types";
import FormButton from "../form-components/FormButton";

const actionShape = PropTypes.shape({
  label: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
});

/**
 * RightSheetFooter — the standard drawer action layout.
 *
 * Destructive action sits left; secondary then primary sit right. The primary is
 * 40px tall with a 140px floor so a single action never looks stranded against
 * the right edge. Renders nothing at all when no action is given, which is how a
 * read-only drawer gets no footer without the consumer branching.
 *
 * The primary is a plain <button> rather than a FormButton because FormButton's
 * `style` prop is its variant name, so there is no way to pass the sizing through
 * it. `border-solid box-border` are deliberate: Toggle.css applies
 * `button { all: unset }` globally, which would otherwise strip the box model.
 */
export default function RightSheetFooter({
  destructive,
  secondary,
  primary,
  loadingLabel = "Submitting...",
}) {
  if (!destructive && !secondary && !primary) return null;

  return (
    <div className="flex items-center justify-between gap-2">
      <div>
        {destructive && (
          <FormButton
            title={destructive.label}
            style="danger"
            onClick={destructive.onClick}
            disabled={destructive.disabled}
          />
        )}
      </div>
      <div className="flex items-center gap-1">
        {secondary && (
          <FormButton
            title={secondary.label}
            style="tertiary"
            onClick={secondary.onClick}
            disabled={secondary.disabled}
          />
        )}
        {primary && (
          <button
            type="button"
            onClick={primary.onClick}
            disabled={primary.disabled || primary.loading}
            style={{ minWidth: 140, height: 40 }}
            className="mx-1 inline-flex items-center justify-center rounded-ctrl border border-transparent border-solid box-border bg-primary px-8 text-base font-medium text-text-inverse hover:bg-primary-hover active:bg-primary-press disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-text-3 cursor-pointer"
          >
            {primary.loading ? loadingLabel : primary.label}
          </button>
        )}
      </div>
    </div>
  );
}

RightSheetFooter.propTypes = {
  destructive: actionShape,
  secondary: actionShape,
  primary: PropTypes.shape({
    label: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
  }),
  loadingLabel: PropTypes.string,
};
